import React, { useRef, useState } from "react";
import { X, PhoneCall, Delete } from "lucide-react";
import { sfx } from "@/lib/soundfx";

const NUMS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0];
const LINES = [
  "Hello? Kaun bol raha hai?",
  "Arre beta, kaise ho?",
  "Number busy hai… thodi der baad karna.",
  "Trin trin… kisi ne nahi uthaya.",
  "Haan bolo bolo, sab theek?",
];
const R = 70; // dial radius
const C = 92; // dial center
const holeAngle = (i) => -112 + i * 28; // spread digits along an arc

export const RotaryPhone = ({ open, onClose }) => {
  const [dialed, setDialed] = useState("");
  const [angle, setAngle] = useState(0);
  const [status, setStatus] = useState("Ghumao dial se number milao…");
  const busy = useRef(false);

  if (!open) return null;

  const dial = (n, i) => {
    if (busy.current || dialed.length >= 8) return;
    busy.current = true;
    const rot = 30 + i * 28; // rotate further for higher positions
    setAngle(rot);
    for (let k = 0; k <= i + 1; k++) setTimeout(() => sfx.click(), 120 + k * 70); // rotary ticks
    setTimeout(() => {
      setAngle(0); // spring back
      setDialed((d) => d + n);
      setStatus("");
      busy.current = false;
    }, 520);
  };

  const call = () => {
    if (!dialed) { setStatus("Pehle number to milao!"); return; }
    setStatus("Trrring… trrring…");
    sfx.phone();
    setTimeout(() => setStatus(LINES[Math.floor(Math.random() * LINES.length)]), 1400);
  };
  const clear = () => { setDialed(""); setStatus("Ghumao dial se number milao…"); };

  return (
    <div data-testid="rotary-phone" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="card-in relative w-full max-w-sm rounded-2xl border-[4px] border-kirana-brown bg-[#f3e6c4] p-4 hard-shadow" onClick={(e) => e.stopPropagation()}>
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-signboard text-xl font-bold text-kirana-brown">☎ STD / PCO Booth</h3>
          <button data-testid="phone-close" onClick={onClose} className="rounded-md border-2 border-kirana-brown bg-kirana-cream p-1 text-kirana-brown"><X className="h-4 w-4" /></button>
        </div>

        {/* number slip */}
        <div className="mb-3 rounded-md border-2 border-kirana-brown bg-[#2F4F3A] px-3 py-1.5">
          <div className="font-mono text-lg tracking-[0.3em] text-kirana-cream" data-testid="phone-dialed">{dialed || "— — —"}</div>
          <div className="min-h-[16px] text-[11px] italic text-kirana-cream/80" data-testid="phone-status">{status}</div>
        </div>

        {/* the phone body */}
        <div className="mx-auto flex flex-col items-center rounded-2xl border-[3px] border-[#3a2617] bg-gradient-to-b from-[#e9a700] to-[#c98a00] p-3">
          {/* handset */}
          <div className="relative mb-2 h-6 w-40">
            <span className="absolute left-0 top-0 h-6 w-8 rounded-full bg-[#2b2b2b]" />
            <span className="absolute right-0 top-0 h-6 w-8 rounded-full bg-[#2b2b2b]" />
            <span className="absolute left-6 top-2 h-2 w-28 rounded-full bg-[#2b2b2b]" />
          </div>

          {/* rotary dial */}
          <div className="relative" style={{ width: C * 2, height: C * 2 }}>
            <div className="absolute inset-0 rounded-full border-[6px] border-[#f3e6c4] bg-[#3a2617]" />
            {/* finger stop */}
            <div className="absolute z-20 h-3 w-6 rounded-sm bg-[#f3e6c4]" style={{ left: C + R + 2, top: C - 6, transform: "rotate(28deg)" }} />
            {/* rotating ring of finger holes */}
            <div className="absolute inset-0" style={{ transform: `rotate(${angle}deg)`, transition: "transform 0.5s cubic-bezier(.34,.1,.24,1)" }}>
              {NUMS.map((n, i) => {
                const a = (holeAngle(i) * Math.PI) / 180;
                const x = C + R * Math.cos(a);
                const y = C + R * Math.sin(a);
                return (
                  <button
                    key={n}
                    data-testid={`dial-${n}`}
                    onClick={() => dial(n, i)}
                    className="absolute flex h-8 w-8 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-[#f3e6c4] bg-[#c98a00] font-bold text-[#3a2617] transition-transform hover:scale-110"
                    style={{ left: x, top: y }}
                  >
                    <span style={{ transform: `rotate(${-angle}deg)` }}>{n}</span>
                  </button>
                );
              })}
            </div>
            <div className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#f3e6c4] bg-[#2b2b2b]" />
          </div>
        </div>

        {/* actions */}
        <div className="mt-3 flex items-center justify-center gap-2">
          <button data-testid="phone-call" onClick={call} className="flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-green px-4 py-2 font-bold text-white transition-transform hover:scale-105 hard-shadow-sm">
            <PhoneCall className="h-4 w-4" /> Ghanti Bajao
          </button>
          <button data-testid="phone-clear" onClick={clear} className="flex items-center gap-2 rounded-md border-[3px] border-kirana-brown bg-kirana-cream px-3 py-2 font-bold text-kirana-brown transition-transform hover:scale-105 hard-shadow-sm">
            <Delete className="h-4 w-4" /> Mita
          </button>
        </div>
        <p className="mt-2 text-center text-[11px] text-kirana-brown/70">Ek minute ₹2 — dial ghumaake number milao 📞</p>
      </div>
    </div>
  );
};
